import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { CheckIcon, LoaderIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
import { DS } from '../ds';
import { AlinovaConstellation, GradientText, SmileDivider } from '../Visuals';
import { Btn, Label, P } from '../Primitives';
const EMAILJS_SERVICE = 'service_g4qrd3j';
const EMAILJS_TEMPLATE = 'template_vuw3rok';
const EMAILJS_KEY = 'a3z7AOkt8EwKXU8c-';
type Status = 'idle' | 'loading' | 'success' | 'error';
interface FormState {
  name: string;
  email: string;
  phone: string;
  modality: string;
}
const modalities = [{
  value: 'fabricacion',
  label: 'Solo fabricación'
}, {
  value: 'integral',
  label: 'Solución integral'
}, {
  value: 'flexible',
  label: 'Pedido flexible'
}, {
  value: 'consulta',
  label: 'Aún tengo dudas'
}];
const inputBase: React.CSSProperties = {
  width: '100%',
  padding: '13px 16px',
  borderRadius: '12px',
  border: `1px solid ${DS.color.border}`,
  background: DS.color.white,
  color: DS.color.ink,
  fontFamily: DS.font.body,
  fontSize: '14px',
  outline: 'none',
  transition: `all 0.22s ${DS.ease.smooth}`,
  boxSizing: 'border-box'
};
const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: DS.font.body,
  fontSize: '12px',
  fontWeight: 600,
  color: DS.color.muted,
  marginBottom: '7px'
};
export const ContactSection = () => {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    modality: 'integral'
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const set = (key: keyof FormState, value: string) => {
    setForm(current => ({
      ...current,
      [key]: value
    }));
    if (errors[key]) setErrors(current => ({
      ...current,
      [key]: undefined
    }));
    if (status === 'error') setStatus('idle');
  };
  const validate = () => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = 'Ingresa tu nombre';
    if (!form.email.trim()) next.email = 'Ingresa tu correo';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Correo no válido';
    if (!form.phone.trim()) next.phone = 'Ingresa un teléfono de contacto';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (status === 'loading' || !validate()) return;
    const modalityLabel = modalities.find(m => m.value === form.modality)?.label ?? form.modality;
    setStatus('loading');
    try {
      await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, {
        from_name: form.name,
        from_email: form.email,
        phone: form.phone,
        modalidad: modalityLabel
      }, EMAILJS_KEY);
      setStatus('success');
    } catch (error) {
      console.error('EmailJS error:', error);
      setStatus('error');
    }
  };
  const focusStyle = (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    event.currentTarget.style.borderColor = DS.color.aqua;
    event.currentTarget.style.background = DS.color.mint;
    event.currentTarget.style.boxShadow = '0 0 0 3px rgba(75,199,210,0.12)';
  };
  const blurStyle = (event: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    event.currentTarget.style.borderColor = DS.color.border;
    event.currentTarget.style.background = DS.color.white;
    event.currentTarget.style.boxShadow = 'none';
  };
  const resetForm = () => {
    setStatus('idle');
    setErrors({});
    setForm({
      name: '',
      email: '',
      phone: '',
      modality: 'integral'
    });
  };
  return <section id="contacto" style={{
    background: DS.color.ivory,
    position: 'relative',
    overflow: 'hidden'
  }} data-id="element-381" data-mp="39duzdifdqikk">
      <SmileDivider fromColor={DS.color.linen} toColor={DS.color.ivory} data-id="element-382" data-mp="13ledu4njiam8" />

      {/* Grid CAD sutil */}
      <div aria-hidden="true" style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: 'linear-gradient(rgba(26,95,115,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(26,95,115,0.025) 1px, transparent 1px)',
      backgroundSize: '48px 48px',
      pointerEvents: 'none',
      zIndex: 0
    }} data-id="element-383" data-mp="17lyehi7bnwd4" />

      <div style={{
      padding: '40px 24px 96px',
      position: 'relative',
      zIndex: 1
    }} data-id="element-384" data-mp="3y1naqm53boi">
        <div style={{
        maxWidth: '1100px',
        margin: '0 auto'
      }} data-id="element-385" data-mp="3w3f945xcnd8u">
          <div className="col-2" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '64px',
          alignItems: 'center'
        }} data-id="element-386" data-mp="3rbx9pmaz5kpz">
            {/* Columna izquierda — texto */}
            <div style={{
            position: 'relative'
          }} data-id="element-387" data-mp="3hbmujw5dq386">
              <AlinovaConstellation width={320} height={320} opacity={0.05} style={{
              position: 'absolute',
              top: '-70px',
              left: '-70px',
              pointerEvents: 'none'
            }} data-id="element-388" data-mp="3cdbmnqyna2sl" />
              <div style={{
              position: 'relative',
              zIndex: 1
            }} data-id="element-389" data-mp="1ruizgfk8wu8f">
                <Label data-id="element-390" data-mp="1wwgoxrj8ol9t">Empecemos</Label>
                <h2 style={{
                fontFamily: DS.font.heading,
                fontWeight: 700,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: DS.color.ink,
                lineHeight: 1.12,
                letterSpacing: '-0.025em',
                marginBottom: '20px'
              }} data-id="element-391" data-mp="1wtcbs4nsqf70">
                  Tu primer caso{' '}
                  <GradientText data-id="element-392" data-mp="1ive2ves8vayw">empieza aquí.</GradientText>
                </h2>
                <P size="lg" maxWidth="420px" data-id="element-393" data-mp="1ixgx6b0fjq9f">
                  Un asesor Alinova te guía en cada paso. Respuesta en menos de 24h.
                </P>

                <a href={`tel:+${DS.phone}`} style={{
                marginTop: '22px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: DS.color.teal,
                textDecoration: 'none',
                fontFamily: DS.font.body,
                fontSize: '14px',
                fontWeight: 700
              }} data-id="element-394" data-mp="qe1yt0vpf06v">
                  <PhoneIcon size={15} aria-hidden="true" data-id="element-395" data-mp="3vm8kf23go72f" />
                  +57 301 653 9065
                </a>

                <div style={{
                marginTop: '36px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }} data-id="element-396" data-mp="3rl6haa6fbav6">
                  {['Respuesta en menos de 24h hábiles', 'Análisis de viabilidad del caso', 'Acompañamiento dedicado en cada etapa'].map(item => <div key={item} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }} data-id="element-397" data-mp="1fu1e4ecv4yh7">
                      <span aria-hidden="true" style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: DS.color.mint,
                    border: `1px solid ${DS.color.aquaLight}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }} data-id="element-398" data-mp="2xro0k08vso73">
                        <CheckIcon size={13} color={DS.color.teal} strokeWidth={3} data-id="element-399" data-mp="37pve093yoo4c" />
                      </span>
                      <span style={{
                    fontFamily: DS.font.body,
                    fontSize: '14px',
                    color: DS.color.body
                  }} data-id="element-400" data-mp="3cooumaejov8q">
                        {item}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Columna derecha — formulario */}
            <div style={{
            background: DS.color.white,
            border: `1px solid ${DS.color.border}`,
            borderRadius: '24px',
            padding: '36px',
            position: 'relative',
            zIndex: 1,
            boxShadow: DS.shadow.card
          }} data-id="element-401" data-mp="20a6tfgdxynmb">
              {status === 'success' ? <div className="anim-fade-in" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              padding: '40px 24px'
            }} data-id="element-402" data-mp="2j3kfevzofjps">
                  <div style={{
                width: '68px',
                height: '68px',
                borderRadius: '50%',
                marginBottom: '24px',
                background: `linear-gradient(135deg, ${DS.color.aquaLight}, ${DS.color.aqua})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: DS.shadow.glow,
                flexShrink: 0
              }} data-id="element-403" data-mp="yn61be83xihl">
                    <CheckIcon size={32} color={DS.color.tealDeep} strokeWidth={3} data-id="element-404" data-mp="3t33oeq0kgmzd" />
                  </div>
                  <h3 style={{
                fontFamily: DS.font.heading,
                fontWeight: 700,
                fontSize: '1.5rem',
                color: DS.color.ink,
                marginBottom: '12px'
              }} data-id="element-405" data-mp="5w1o4ip0kkd3">
                    ¡Caso recibido!
                  </h3>
                  <p style={{
                fontFamily: DS.font.body,
                fontSize: '15px',
                color: DS.color.muted,
                lineHeight: 1.6,
                maxWidth: '300px',
                margin: '0 auto'
              }} data-id="element-406" data-mp="2cmrcrjmk3j7i">
                    Gracias, {form.name.split(' ')[0] || 'doctor(a)'}. Un asesor de Alinova te contactará muy pronto.
                  </p>
                  <div style={{
                marginTop: '28px'
              }} data-id="element-407" data-mp="3ictzufan2yxj">
                    <Btn variant="ghost" size="md" onClick={resetForm} data-id="element-408" data-mp="3nc6j6q2axnvd">
                      Enviar otro caso
                    </Btn>
                  </div>
                </div> : <form onSubmit={handleSubmit} noValidate data-id="element-409" data-mp="xetv70rldgc">
                  {status === 'error' && <div role="alert" style={{
                marginBottom: '16px',
                padding: '12px 16px',
                borderRadius: '10px',
                background: 'rgba(224,106,106,0.08)',
                border: '1px solid rgba(224,106,106,0.25)',
                fontFamily: DS.font.body,
                fontSize: '13px',
                color: '#c0504d',
                textAlign: 'center'
              }} data-id="element-410" data-mp="5whyiqcu1hns">
                      Hubo un error al enviar. Por favor intenta de nuevo.
                    </div>}

                  <div style={{
                marginBottom: '14px'
              }} data-id="element-411" data-mp="2948jrapnv880">
                    <label htmlFor="cf-name" style={labelStyle} data-id="element-412" data-mp="296ushdoaftrt">Nombre *</label>
                    <input id="cf-name" value={form.name} onChange={e => set('name', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} placeholder="Dr(a). Nombre completo" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'cf-name-error' : undefined} style={{
                  ...inputBase,
                  borderColor: errors.name ? '#e06a6a' : DS.color.border
                }} data-id="element-413" data-mp="24bxmkfmb20j5" />
                    {errors.name && <span id="cf-name-error" style={errorStyle} data-id="element-414" data-mp="ldj8xdy0rv8d">{errors.name}</span>}
                  </div>

                  <div className="col-2" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '14px',
                marginBottom: '14px'
              }} data-id="element-415" data-mp="6k6801tw28kx">
                    <div data-id="element-416" data-mp="1rv6c1i60wqwn">
                      <label htmlFor="cf-email" style={labelStyle} data-id="element-417" data-mp="1ruwkhq5bybou">Correo *</label>
                      <input id="cf-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} placeholder="tu@correo.com" aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'cf-email-error' : undefined} style={{
                    ...inputBase,
                    borderColor: errors.email ? '#e06a6a' : DS.color.border
                  }} data-id="element-418" data-mp="1wtzdudby6tgz" />
                      {errors.email && <span id="cf-email-error" style={errorStyle} data-id="element-419" data-mp="2yoklvrjfi1wk">{errors.email}</span>}
                    </div>
                    <div data-id="element-420" data-mp="14b2vzxvjwsyd">
                      <label htmlFor="cf-phone" style={labelStyle} data-id="element-421" data-mp="14aktwkm7jkij">Teléfono *</label>
                      <input id="cf-phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} placeholder="Tu número de contacto" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'cf-phone-error' : undefined} style={{
                    ...inputBase,
                    borderColor: errors.phone ? '#e06a6a' : DS.color.border
                  }} data-id="element-422" data-mp="10a96j02gn63d" />
                      {errors.phone && <span id="cf-phone-error" style={errorStyle} data-id="element-423" data-mp="rz0k7nf1ty0e">{errors.phone}</span>}
                    </div>
                  </div>

                  <div style={{
                marginBottom: '28px'
              }} data-id="element-424" data-mp="3vh26v2p6ddam">
                    <label htmlFor="cf-modality" style={labelStyle} data-id="element-425" data-mp="4b95kd61d8vy">Modalidad de interés</label>
                    <select id="cf-modality" value={form.modality} onChange={e => set('modality', e.target.value)} onFocus={focusStyle} onBlur={blurStyle} style={{
                  ...inputBase,
                  cursor: 'pointer',
                  appearance: 'none',
                  color: DS.color.ink
                }} data-id="element-426" data-mp="3sxdu3d8d9av4">
                      {modalities.map(m => <option key={m.value} value={m.value} style={{
                    color: DS.color.ink
                  }} data-id="element-427" data-mp="356i84wqld9vs">
                          {m.label}
                        </option>)}
                    </select>
                  </div>

                  <Btn variant="aqua" size="lg" fullWidth type="submit" disabled={status === 'loading'} data-id="element-428" data-mp="2r7sanfzv186w">
                    {status === 'loading' ? <><LoaderIcon size={18} className="anim-rotate" data-id="element-429" data-mp="2m9qtj0lzviy2" /> Enviando...</> : 'Empezar mi primer caso'}
                  </Btn>

                  <a href={DS.wa} target="_blank" rel="noopener noreferrer" style={{
                marginTop: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '9px',
                color: DS.color.teal,
                fontFamily: DS.font.body,
                fontSize: '14px',
                fontWeight: 700,
                textDecoration: 'none',
                opacity: 0.85,
                transition: `opacity 0.2s ${DS.ease.smooth}`
              }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.85'} data-id="element-430" data-mp="2go9jqsg70ya5">
                    <MessageCircleIcon size={17} aria-hidden="true" data-id="element-431" data-mp="17klpevku07y2" />
                    Prefiero continuar por WhatsApp
                  </a>
                </form>}
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const errorStyle: React.CSSProperties = {
  fontFamily: DS.font.body,
  fontSize: '11px',
  color: '#c0504d'
};